import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faSave,
  faEdit,
  faPlus,
  faTrash,
  faTimes,
  faCalendar,
  faBusinessTime,
  faExclamationTriangle,
  faCheckCircle,
  faCopy,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';

const WorkingHoursSettingsPage: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('default');

  const breadcrumb = [{ label: 'Nastavení' }, { label: 'Pracovní doby' }];

  const days = [
    { id: 'monday', name: 'Pondělí', short: 'Po' },
    { id: 'tuesday', name: 'Úterý', short: 'Út' },
    { id: 'wednesday', name: 'Středa', short: 'St' },
    { id: 'thursday', name: 'Čtvrtek', short: 'Čt' },
    { id: 'friday', name: 'Pátek', short: 'Pá' },
    { id: 'saturday', name: 'Sobota', short: 'So' },
    { id: 'sunday', name: 'Neděle', short: 'Ne' },
  ];

  const workingHoursTemplates = [
    {
      id: 'default',
      name: 'Standardní doby',
      description: 'Základní pracovní doby salonu',
      isDefault: true,
      hours: {
        monday: { start: '08:00', end: '18:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        tuesday: { start: '08:00', end: '18:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        wednesday: { start: '08:00', end: '18:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        thursday: { start: '08:00', end: '18:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        friday: { start: '08:00', end: '18:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        saturday: { start: '09:00', end: '16:00', isWorking: true, breakStart: null, breakEnd: null },
        sunday: { start: '09:00', end: '16:00', isWorking: false, breakStart: null, breakEnd: null },
      },
    },
    {
      id: 'extended',
      name: 'Rozšířené doby',
      description: 'Delší pracovní doby pro víkendy',
      isDefault: false,
      hours: {
        monday: { start: '07:00', end: '19:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        tuesday: { start: '07:00', end: '19:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        wednesday: { start: '07:00', end: '19:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        thursday: { start: '07:00', end: '19:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        friday: { start: '07:00', end: '19:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        saturday: { start: '08:00', end: '18:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        sunday: { start: '09:00', end: '17:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
      },
    },
    {
      id: 'weekend',
      name: 'Víkendový režim',
      description: 'Speciální doby pro víkendy',
      isDefault: false,
      hours: {
        monday: { start: '08:00', end: '18:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        tuesday: { start: '08:00', end: '18:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        wednesday: { start: '08:00', end: '18:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        thursday: { start: '08:00', end: '18:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        friday: { start: '08:00', end: '18:00', isWorking: true, breakStart: '12:00', breakEnd: '13:00' },
        saturday: { start: '10:00', end: '20:00', isWorking: true, breakStart: '14:00', breakEnd: '15:00' },
        sunday: { start: '10:00', end: '20:00', isWorking: true, breakStart: '14:00', breakEnd: '15:00' },
      },
    },
  ];

  const specialHours = [
    {
      id: '1',
      name: 'Vánoční svátky',
      date: '2024-12-24',
      isWorking: false,
      note: 'Zavřeno kvůli vánočním svátkům',
    },
    {
      id: '2',
      name: 'Nový rok',
      date: '2025-01-01',
      isWorking: false,
      note: 'Zavřeno kvůli novoročním svátkům',
    },
    {
      id: '3',
      name: 'Valentýnský den',
      date: '2025-02-14',
      isWorking: true,
      start: '09:00',
      end: '21:00',
      note: 'Rozšířené doby pro Valentýna',
    },
  ];

  const currentTemplate = workingHoursTemplates.find(t => t.id === selectedTemplate) || workingHoursTemplates[0];

  const handleTimeChange = (day: string, field: string, value: string) => {
    // Handle time changes for current template
    console.log(`Changing ${day}.${field} to ${value}`);
  };

  const handleWorkingToggle = (day: string, isWorking: boolean) => {
    // Handle working day toggle
    console.log(`Setting ${day} working to ${isWorking}`);
  };

  const handleSave = () => {
    console.log('Saving working hours settings');
    setIsEditing(false);
  };

  const calculateWorkingHours = (dayHours: any) => {
    if (!dayHours.isWorking) return '0h';

    const start = new Date(`2000-01-01T${dayHours.start}:00`);
    const end = new Date(`2000-01-01T${dayHours.end}:00`);
    const diffMs = end.getTime() - start.getTime();

    let totalHours = diffMs / (1000 * 60 * 60);

    // Subtract break time if exists
    if (dayHours.breakStart && dayHours.breakEnd) {
      const breakStart = new Date(`2000-01-01T${dayHours.breakStart}:00`);
      const breakEnd = new Date(`2000-01-01T${dayHours.breakEnd}:00`);
      const breakMs = breakEnd.getTime() - breakStart.getTime();
      const breakHours = breakMs / (1000 * 60 * 60);
      totalHours -= breakHours;
    }

    return `${totalHours}h`;
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Pracovní doby" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Nastavení pracovních dob</h1>
                <p className="text-gray-600 mt-1">Spravujte pracovní doby a speciální režimy</p>
              </div>
              <div className="flex items-center space-x-3">
                {!isEditing ? (
                  <button onClick={() => setIsEditing(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                    Upravit
                  </button>
                ) : (
                  <>
                    <button onClick={() => setIsEditing(false)} className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
                      <FontAwesomeIcon icon={faTimes} className="mr-2" />
                      Zrušit
                    </button>
                    <button onClick={handleSave} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      Uložit
                    </button>
                  </>
                )}
              </div>
            </div>
          </section>

          {/* Template Selection */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Šablony pracovních dob</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {workingHoursTemplates.map(template => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-4 rounded-lg border-2 transition-colors text-left ${
                    selectedTemplate === template.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">{template.name}</h4>
                    {template.isDefault && <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Výchozí</span>}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <FontAwesomeIcon icon={faClock} className="w-4 h-4 mr-1" />
                    {Object.values(template.hours).filter(h => h.isWorking).length}/7 dní
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Working Hours Table */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Pracovní doby - {currentTemplate.name}</h3>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Den</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pracuje</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Začátek</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Konec</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Přestávka</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Celkem hodin</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {days.map(day => {
                      const dayHours = currentTemplate.hours[day.id as keyof typeof currentTemplate.hours];

                      return (
                        <tr key={day.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <FontAwesomeIcon icon={faCalendar} className="w-4 h-4 mr-2 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">{day.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isEditing ? (
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={dayHours.isWorking}
                                  onChange={e => handleWorkingToggle(day.id, e.target.checked)}
                                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <span className="ml-2 text-sm text-gray-900">{dayHours.isWorking ? 'Ano' : 'Ne'}</span>
                              </label>
                            ) : (
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  dayHours.isWorking ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}
                              >
                                <FontAwesomeIcon icon={dayHours.isWorking ? faCheckCircle : faExclamationTriangle} className="w-3 h-3 mr-1" />
                                {dayHours.isWorking ? 'Ano' : 'Ne'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isEditing ? (
                              <input
                                type="time"
                                value={dayHours.start}
                                onChange={e => handleTimeChange(day.id, 'start', e.target.value)}
                                disabled={!dayHours.isWorking}
                                className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 disabled:bg-gray-100"
                              />
                            ) : (
                              <div className="flex items-center">
                                <FontAwesomeIcon icon={faClock} className="w-4 h-4 mr-2 text-gray-400" />
                                <span className="text-sm text-gray-900">{dayHours.isWorking ? dayHours.start : '-'}</span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isEditing ? (
                              <input
                                type="time"
                                value={dayHours.end}
                                onChange={e => handleTimeChange(day.id, 'end', e.target.value)}
                                disabled={!dayHours.isWorking}
                                className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 disabled:bg-gray-100"
                              />
                            ) : (
                              <div className="flex items-center">
                                <FontAwesomeIcon icon={faClock} className="w-4 h-4 mr-2 text-gray-400" />
                                <span className="text-sm text-gray-900">{dayHours.isWorking ? dayHours.end : '-'}</span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isEditing ? (
                              <div className="flex items-center space-x-1">
                                <input
                                  type="time"
                                  value={dayHours.breakStart || ''}
                                  onChange={e => handleTimeChange(day.id, 'breakStart', e.target.value)}
                                  disabled={!dayHours.isWorking}
                                  className="px-2 py-1 border border-gray-300 rounded text-xs disabled:bg-gray-100"
                                />
                                <span className="text-gray-500">-</span>
                                <input
                                  type="time"
                                  value={dayHours.breakEnd || ''}
                                  onChange={e => handleTimeChange(day.id, 'breakEnd', e.target.value)}
                                  disabled={!dayHours.isWorking}
                                  className="px-2 py-1 border border-gray-300 rounded text-xs disabled:bg-gray-100"
                                />
                              </div>
                            ) : (
                              <span className="text-sm text-gray-900">
                                {dayHours.breakStart && dayHours.breakEnd ? `${dayHours.breakStart} - ${dayHours.breakEnd}` : 'Bez přestávky'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">{calculateWorkingHours(dayHours)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faBusinessTime} className="w-5 h-5 mr-2 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Celkem pracovních hodin týdně:</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">
                    {days
                      .reduce((total, day) => {
                        const dayHours = currentTemplate.hours[day.id as keyof typeof currentTemplate.hours];
                        if (dayHours.isWorking) {
                          const start = new Date(`2000-01-01T${dayHours.start}:00`);
                          const end = new Date(`2000-01-01T${dayHours.end}:00`);
                          const diffMs = end.getTime() - start.getTime();
                          let diffHours = diffMs / (1000 * 60 * 60);

                          if (dayHours.breakStart && dayHours.breakEnd) {
                            const breakStart = new Date(`2000-01-01T${dayHours.breakStart}:00`);
                            const breakEnd = new Date(`2000-01-01T${dayHours.breakEnd}:00`);
                            const breakMs = breakEnd.getTime() - breakStart.getTime();
                            const breakHours = breakMs / (1000 * 60 * 60);
                            diffHours -= breakHours;
                          }

                          return total + diffHours;
                        }
                        return total;
                      }, 0)
                      .toFixed(1)}
                    h
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Special Hours */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Speciální pracovní doby</h3>
                <button className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm">
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Přidat speciální den
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {specialHours.map(special => (
                  <div key={special.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="w-6 h-6 text-yellow-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{special.name}</h4>
                        <p className="text-sm text-gray-600">{new Date(special.date).toLocaleDateString('cs-CZ')}</p>
                        <p className="text-sm text-gray-500">{special.note}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {special.isWorking ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                          {special.start} - {special.end}
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-full">Zavřeno</span>
                      )}
                      <button className="text-gray-400 hover:text-blue-600 p-1">
                        <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                      </button>
                      <button className="text-gray-400 hover:text-red-600 p-1">
                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <FontAwesomeIcon icon={faCopy} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Duplikovat šablonu</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <FontAwesomeIcon icon={faCalendar} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Nastavit jako výchozí</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <FontAwesomeIcon icon={faBusinessTime} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Export do kalendáře</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Export nastavení</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default WorkingHoursSettingsPage;
